<template>
  <section
    v-if="isLoading || items.length"
    class="ca-bullet-container ca-key-information"
  >
    <div v-if="showDivider" class="ca-key-information__divider"></div>

    <ul v-if="isLoading" class="ca-key-information__list">
      <li v-for="n in PLACEHOLDER_COUNT" :key="n" class="ca-key-information__item">
        <span
          v-skeleton
          skeleton-radius="150"
          class="ca-key-information__avatar ca-key-information__avatar--placeholder"
          aria-hidden="true"
        ></span>

        <div class="ca-key-information__content">
          <p v-skeleton class="ca-key-information__title heading-jumbo">&nbsp;</p>
          <p v-skeleton class="ca-key-information__description paragraph-bold">
            &nbsp;
          </p>
        </div>
      </li>
    </ul>

    <ul v-else class="ca-key-information__list">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="ca-key-information__item"
      >
        <span
          v-if="item.iconUrl"
          class="ca-key-information__avatar"
          aria-hidden="true"
        >
          <RoundedAvatarV2
            size="56px"
            :image="item.iconUrl"
            :backgroundColorClass="avatarBgClass"
            imageClass="bg-no-repeat bg-auto"
          />
        </span>
        <span
          v-else-if="hasAnyIcon"
          class="ca-key-information__avatar-spacer"
          aria-hidden="true"
        ></span>

        <div class="ca-key-information__content">
          <component
            :is="normalizeHeadingTag(item.titleHeadingType, 'p')"
            class="ca-key-information__title heading-jumbo text-text-headers"
          >
            {{ item.title }}
          </component>
          <component
            v-if="item.description"
            :is="normalizeHeadingTag(item.descriptionHeadingType, 'p')"
            class="ca-key-information__description paragraph-bold text-text-body"
          >
            {{ item.description }}
          </component>
        </div>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { RoundedAvatarV2 } from "ca-online-design-system";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaKeyInformation",
});

const isDev = import.meta.env.DEV;

const MAX_ITEMS = 3;
const PLACEHOLDER_COUNT = 3;

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const items = computed(() => (model.value.items ?? []).slice(0, MAX_ITEMS));

const isLoading = computed(() => modelRef.value === null);

const hasAnyIcon = computed(() => items.value.some((item) => item.iconUrl));

const showDivider = computed(() =>
  isLoading.value ? true : Boolean(model.value.showDivider),
);

const avatarBgClass = computed(() =>
  model.value.whiteAvatar ? "backgrounds-surface" : "backgrounds-container-high",
);

onMounted(async () => {
  if (isDev && !modelRef.value) {
    const { mockKeyInformation3 } = await import(
      "./mocks/keyInformationMocks.js"
    );
    modelRef.value = mockKeyInformation3;
  }
});
</script>

<style scoped lang="scss">
.ca-key-information {
  padding-block: var(--size-5);
}

.ca-key-information__divider {
  display: flex;
  align-items: center;
  height: 16px;
}

.ca-key-information__divider::before {
  content: "";
  display: block;
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.2);
}

.ca-key-information__list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--size-7);
  margin: 0;
  padding-block: var(--size-7);
  padding-inline: 0;
  list-style: none;
}

.ca-key-information__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--size-5);
  width: 100%;
  text-align: center;
  word-break: break-word;
}

.ca-key-information__avatar {
  display: flex;
  flex-shrink: 0;
}

.ca-key-information__avatar--placeholder {
  width: 56px;
  height: 56px;
}

.ca-key-information__avatar-spacer {
  display: none;
}

.ca-key-information__content {
  width: 100%;
}

.ca-key-information__title,
.ca-key-information__description {
  margin: 0;
}

@include ca-bp("sm") {
  .ca-key-information__list {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: var(--size-5);
    padding-block: var(--size-9);
  }

  .ca-key-information__item {
    flex: 1 1 0;
    min-width: 0;
    max-width: calc((100% - 2 * var(--size-5)) / 3);
  }

  .ca-key-information__avatar-spacer {
    display: block;
    height: 56px;
  }
}

@include ca-bp("md") {
  .ca-key-information {
    padding-block: var(--size-6);
  }
}

@include ca-bp("lg") {
  .ca-key-information {
    padding-block: var(--size-8);
  }
}
</style>
